import { Button, Field, Input, MachineRaroBrand, Notice, Serif } from '@raro/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation } from 'react-router';
import { GoogleButton } from '../components/GoogleButton';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import ocean from '../assets/ocean.jpg';

const NEXT_KEY = 'machine.raro.next';
function rememberNext(path: string) { taken = undefined; try { sessionStorage.setItem(NEXT_KEY, path); } catch { /* sem storage */ } }
/** lido uma vez por login e guardado aqui: a renderização pode repetir, e a segunda leitura acharia o storage vazio */
let taken: string | null | undefined;
/** o caminho guardado antes do login (uma vez só), se for daqui mesmo */
function nextAfterLogin() {
  if (taken === undefined) {
    try {
      const p = sessionStorage.getItem(NEXT_KEY);
      sessionStorage.removeItem(NEXT_KEY);
      taken = p && p.startsWith('/') && !p.startsWith('//') ? p : null;
    } catch { taken = null; }
  }
  return taken;
}
/** já entrou e chegou: o próximo login começa do zero */
export function forgetNext() { taken = undefined; }

export function LoginPage({ mode = 'login' }: { mode?: 'login' | 'bootstrap' }) {
  const { me, login, acceptToken, bootstrap } = useAuth();
  // a Machine é de uso interno: não há cadastro. "primeira vez" só existe enquanto a base está vazia.
  const config = useQuery({ queryKey: ['auth-config'], queryFn: () => api.get<{ initialized: boolean; googleClientId: string | null }>('/auth/config'), staleTime: 60_000 });
  const location = useLocation() as { state?: { from?: string } };
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // o login com Google sai da página e perde o "de onde veio": guarda antes, para voltar (a conexão com o Claude depende disso)
  useEffect(() => { if (location.state?.from) rememberNext(location.state.from); }, [location.state?.from]);

  // volta do Google: o token vem no fragmento (ou o motivo da recusa na query). hooks sempre antes de qualquer retorno antecipado
  useEffect(() => {
    const token = new URLSearchParams(window.location.hash.slice(1)).get('google');
    const erro = new URLSearchParams(window.location.search).get('erro');
    if (!token && !erro) return;
    window.history.replaceState(null, '', window.location.pathname);
    if (token) acceptToken(token);
    else if (erro) setError(erro);
  }, [acceptToken]);

  if (me) return <Navigate to={location.state?.from ?? nextAfterLogin() ?? '/'} replace />;
  if (mode === 'bootstrap' && config.data?.initialized) return <Navigate to="/login" replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'bootstrap') await bootstrap({ email, name, password });
      else await login(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível entrar');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rr-auth">
      <section className="rr-auth__hero">
        <img className="rr-auth__hero-img" src={ocean} alt="" aria-hidden="true" />
        <MachineRaroBrand size="xl" />
        <div className="rr-stack rr-stack--3">
          <p className="rr-heading">
            tornar visível
            <br />o que é visível.
          </p>
          <div className="rr-auth__hero-rule" />
          <p className="rr-muted" style={{ maxWidth: 380 }}>
            O sistema operacional intelectual da Raro.
            <br />
            <strong style={{ fontWeight: 500, color: 'var(--rr-text)' }}>Mais contexto. Mais pergunta.</strong>
            <br />
            Mais verdade. Menos óbvio.
          </p>
        </div>
      </section>
      <section className="rr-auth__panel">
        <form className="rr-auth__form rr-form" onSubmit={submit}>
          <div className="rr-stack rr-stack--2">
            <span className="rr-label">{mode === 'bootstrap' ? 'primeira vez' : 'entrar'}</span>
            <h1 className="rr-title">
              {mode === 'bootstrap' ? (
                <>
                  Inicializar a <Serif italic>Machine</Serif>.
                </>
              ) : (
                <>
                  O que precisamos <Serif italic>enxergar</Serif> hoje?
                </>
              )}
            </h1>
          </div>
          {mode === 'bootstrap' && (
            <Field label="Seu nome" htmlFor="name">
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} />
            </Field>
          )}
          <Field label="E-mail" htmlFor="email">
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
          </Field>
          <Field label="Senha" htmlFor="password" hint={mode === 'bootstrap' ? 'mínimo de 8 caracteres' : undefined}>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={mode === 'bootstrap' ? 8 : 1}
            />
          </Field>
          {error && <Notice tone="error">{error}</Notice>}
          <div className="rr-form__actions">
            <Button variant="primary" type="submit" disabled={busy}>
              {busy ? '…' : mode === 'bootstrap' ? 'criar a Raro' : 'entrar'}
            </Button>
            {mode === 'login' ? (
              <span className="rr-row" style={{ gap: 'var(--rr-space-3)', fontSize: 'var(--rr-text-xs)' }}>
                <Link className="rr-faint" to="/senha">esqueci a senha</Link>
                {config.data && !config.data.initialized && <Link className="rr-faint" to="/inicializar">primeira vez</Link>}
              </span>
            ) : (
              <Link className="rr-faint" to="/login" style={{ fontSize: 'var(--rr-text-xs)' }}>
                já tenho acesso
              </Link>
            )}
          </div>
          {mode === 'login' && config.data?.googleClientId && (
            <div className="rr-auth__alt">
              <span className="rr-auth__or">ou</span>
              <GoogleButton clientId={config.data.googleClientId} onError={setError} />
            </div>
          )}
          {mode === 'login' && <p className="rr-faint rr-auth__note">acesso exclusivo da Raro e de seus clientes, por convite.</p>}
        </form>
      </section>
    </div>
  );
}
